import { useGoogleMap } from "@/GoogleMapContext";
import { useAppDispatch } from "@/store/hooks";
import type { RootState } from "@/store/rootReducer";
import { getAddress } from "@/store/slices/customerSlice";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import {
  Autocomplete,
  GoogleMap,
  Marker,
} from "@react-google-maps/api";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

interface ViewLocationProps {
  modalOpen: boolean;
  closeModal: () => void;
  title: string;
  lat?: string | number;
  lng?: string | number;
  width?: string;
  searchable?: boolean;
  isShowDoneButton?: boolean;
  usePrivateAddressApi?: boolean;
  onDone: (data: {
    address: string;
    lat: number;
    lng: number;
  }) => void;
}

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
};

const DEFAULT_ZOOM = 16;

const AddressDialog = ({
  modalOpen,
  closeModal,
  title,
  lat,
  lng,
  width,
  searchable,
  isShowDoneButton = true,
  usePrivateAddressApi = true,
  onDone,
}: ViewLocationProps) => {
  const dispatch = useAppDispatch();
  const { address: storeAddress } = useSelector((state: RootState) => state.customer);
  const { isLoaded, loadError } = useGoogleMap();

  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState("");

  const [markerPos, setMarkerPos] = useState<google.maps.LatLngLiteral | null>(null);
  const [latValue, setLatValue] = useState("");
  const [lngValue, setLngValue] = useState("");

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const initialized = useRef(false);
  const skipDebounce = useRef(false);

  useEffect(() => {
    if (!usePrivateAddressApi) return;

    if (storeAddress) {
      setAddress(storeAddress);
    }
  }, [storeAddress, usePrivateAddressApi]);

  const reverseGeocode = useCallback((pos: google.maps.LatLngLiteral) => {
    if (!window.google?.maps) return;

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: pos }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        setAddress(results[0]?.formatted_address ?? "");
      } else {
        setAddress("");
      }
    });
  }, []);

  const fetchAddress = useCallback((pos: google.maps.LatLngLiteral) => {
    if (usePrivateAddressApi) {
      dispatch(getAddress({ latitude: pos.lat, longitude: pos.lng }));
    } else {
      reverseGeocode(pos);
    }
  }, [dispatch, usePrivateAddressApi, reverseGeocode]);

  const setLocation = useCallback((pos: google.maps.LatLngLiteral) => {
    const latitude = parseFloat(pos.lat.toFixed(6));
    const longitude = parseFloat(pos.lng.toFixed(6));

    const location = {
      lat: latitude,
      lng: longitude,
    };

    setMarkerPos(location);
    setLatValue(latitude.toFixed(6));
    setLngValue(longitude.toFixed(6));
    fetchAddress(location);
    mapRef.current?.panTo(location);
  }, [fetchAddress]);

  useEffect(() => {
    if (!modalOpen || !isLoaded) return;

    if (initialized.current) return;

    initialized.current = true;

    setLoading(true);

    // edit mode
    if (lat && lng) {
      const position = {
        lat: parseFloat(Number(lat).toFixed(6)),
        lng: parseFloat(Number(lng).toFixed(6)),
      };

      setLocation(position);
      setLoading(false);
      return;
    }

    // Add mode
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        setLoading(false);
      },
      () => {
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  }, [modalOpen, isLoaded, lat, lng, fetchAddress]);

  useEffect(() => {
    if (!modalOpen) {
      initialized.current = false;
    }
  }, [modalOpen]);

  const updateLocation = useCallback((pos: google.maps.LatLngLiteral, moveMap = true, fetch = true, updateTextbox = true) => {
    const location = {
      lat: parseFloat(pos.lat.toFixed(6)),
      lng: parseFloat(pos.lng.toFixed(6)),
    };

    if (updateTextbox) {
      skipDebounce.current = true;

      setLatValue(location.lat.toFixed(6));
      setLngValue(location.lng.toFixed(6));
    }

    setMarkerPos(location);

    if (moveMap) {
      mapRef.current?.panTo(location);
    }

    if (fetch) {
      fetchAddress(location);
    }
  }, [fetchAddress]);

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    updateLocation({
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });
  };

  const handleMapChange = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    updateLocation({
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();

    if (!place?.geometry?.location) return;

    const pos = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    updateLocation(pos);

    if (place.formatted_address) {
      setAddress(place.formatted_address);
    }
  };

  useEffect(() => {
    if (!latValue || !lngValue) return;

    if (skipDebounce.current) {
      skipDebounce.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const latitude = Number(latValue);
      const longitude = Number(lngValue);

      if (isNaN(latitude) || isNaN(longitude)) return;

      if (latitude < -90 || latitude > 90) return;
      if (longitude < -180 || longitude > 180) return;

      updateLocation(
        {
          lat: latitude,
          lng: longitude,
        },
        true,
        true,
        false
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [latValue, lngValue, updateLocation]);

  if (loadError) return <p>Map failed to load</p>;

  if (!isLoaded || loading) {
    return (
      <Dialog open={modalOpen} fullWidth>
        <DialogContent sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!markerPos) return null;

  return (
    <Dialog
      open={modalOpen}
      onClose={closeModal}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: { style: { width: width || "600px" } },
      }}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <GoogleMap
          center={markerPos}
          zoom={DEFAULT_ZOOM}
          mapContainerStyle={mapContainerStyle}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={handleMapChange}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
          }}
        >
          <Marker
            position={markerPos}
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        </GoogleMap>

        <Box mt={2}>
          {searchable && (
            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={handlePlaceChanged}
            >
              <TextField
                fullWidth
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                label="Search Address"
                placeholder="Search location"
                variant="standard"
                size="small"
                sx={{ mb: 2 }}
              />
            </Autocomplete>
          )}

          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Latitude"
              variant="standard"
              type="number"
              slotProps={{
                htmlInput: {
                  step: "0.000001",
                },
              }}
              value={latValue}
              onChange={(e) => setLatValue(e.target.value)}
            />

            <TextField
              fullWidth
              label="Longitude"
              variant="standard"
              value={lngValue}
              type="number"
              slotProps={{
                htmlInput: {
                  step: "0.000001",
                },
              }}
              onChange={(e) => setLngValue(e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeModal} style={{ color: "black" }}>
          Cancel
        </Button>
        {isShowDoneButton && (
          <Button
            variant="contained"
            onClick={() => {
              if (!markerPos) return;

              onDone({
                address,
                lat: markerPos.lat,
                lng: markerPos.lng,
              });

              closeModal();
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddressDialog);
